import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { BackgroundColorContainer, Heading } from '../../atoms';
import { RedirectToLogin } from '../../router';
import { usePayment, createPaymentToken } from '../../payment';
import { useSessionCustomer } from '../../customer';
import { useReporter } from '../../reporter';
import { TOAST_SERVER_MESSAGE } from '../constants';
import { useToast, TOAST_CLIENT_ERROR_MESSAGE } from '../../Toast';
import { EditPaymentForm } from '../EditPaymentForm';
import { ApiResult } from '../../api';
import { AccountHeader } from '../AccountHeader';
import { Footer, LoadingView } from '../../components';
import { Container, HeadingContainer } from './styles';
import { userViewEditPayment } from './events';

export const EditPaymentContainer = ({
  footerContent,
  headerTitleLabel,
  labelWithStripe,
  labelNameCard,
  labelCardNumber,
  labelExpirationDate,
  labelCVC,
  placeholderNameCard,
  placeholderCardNumber,
  placeholderExpirationDate,
  placeholderCVC,
  saveBtnLabel,
  cancelBtnLabel,
  cancelBtnTarget,
  links,
  logoUrl,
  logoSrText,
  onPaymentChanged,
}) => {
  const {
    customer,
    isActive,
    isLoading: isCustomerLoading,
  } = useSessionCustomer();
  const { create: createPayment } = usePayment(customer?.id);
  const stripe = useStripe();
  const elements = useElements();
  const reporter = useReporter();
  const { showToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isCustomerLoading) {
      reporter.tag(userViewEditPayment());
    }
  }, [isCustomerLoading, reporter]);

  const onSubmit = async paymentMethod => {
    setIsSubmitting(true);

    const tokenResult = await createPaymentToken({ elements, stripe });

    const paymentMethodResult = await ApiResult.flatMapAsync(
      tokenResult,
      token =>
        createPayment({
          last_four_digits: token.card.last4,
          provider_token: token.id,
          is_default: true,
        }),
    );
    setIsSubmitting(false);
    ApiResult.match(paymentMethodResult, {
      success: () => {
        onPaymentChanged();
        showToast({
          status: 'success',
          message: 'Your payment method has been successfully updated.',
          headline: 'Success',
        });
      },
      error: {
        server: () => {
          showToast({
            status: 'error',
            message: TOAST_SERVER_MESSAGE,
            headline: 'Error Message',
          });
        },
        client: error => {
          reporter.error(error.original);
          showToast({
            status: 'error',
            message: TOAST_CLIENT_ERROR_MESSAGE,
            headline: 'Error Message',
          });
        },
      },
    });

    return paymentMethodResult;
  };

  if (isCustomerLoading) {
    return <LoadingView />;
  }

  if (!isActive) {
    return <RedirectToLogin />;
  }

  return (
    <>
      <AccountHeader
        links={links}
        logoUrl={logoUrl}
        logoSrText={logoSrText}
        name={customer.first_name}
      />
      <BackgroundColorContainer bgColor="background.base">
        <Container>
          <HeadingContainer>
            <Heading
              headingLevel="h3"
              typography="heading1"
              headingText={headerTitleLabel}
              position="center"
            />
          </HeadingContainer>
          <EditPaymentForm
            labelWithStripe={labelWithStripe}
            labelNameCard={labelNameCard}
            labelCardNumber={labelCardNumber}
            labelExpirationDate={labelExpirationDate}
            labelCVC={labelCVC}
            placeholderNameCard={placeholderNameCard}
            placeholderCardNumber={placeholderCardNumber}
            placeholderExpirationDate={placeholderExpirationDate}
            placeholderCVC={placeholderCVC}
            saveBtnLabel={saveBtnLabel}
            cancelBtnLabel={cancelBtnLabel}
            cancelBtnTarget={cancelBtnTarget}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
          />
        </Container>
      </BackgroundColorContainer>
      <Footer {...footerContent} />
    </>
  );
};

EditPaymentContainer.displayName = 'EditPaymentContainer';
EditPaymentContainer.propTypes = {
  headerTitleLabel: PropTypes.string.isRequired,
  labelWithStripe: PropTypes.string.isRequired,
  labelNameCard: PropTypes.string.isRequired,
  labelCardNumber: PropTypes.string.isRequired,
  labelExpirationDate: PropTypes.string.isRequired,
  labelCVC: PropTypes.string.isRequired,
  placeholderNameCard: PropTypes.string.isRequired,
  placeholderCardNumber: PropTypes.string.isRequired,
  placeholderExpirationDate: PropTypes.string.isRequired,
  placeholderCVC: PropTypes.string.isRequired,
  saveBtnLabel: PropTypes.string.isRequired,
  cancelBtnLabel: PropTypes.string.isRequired,
  cancelBtnTarget: PropTypes.string.isRequired,
  links: PropTypes.array.isRequired,
  logoUrl: PropTypes.string.isRequired,
  logoSrText: PropTypes.string.isRequired,
  onPaymentChanged: PropTypes.func.isRequired,
  footerContent: PropTypes.object.isRequired,
};
